import { Component, Input, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BreadcrumbService } from './breadcrumb.service';
import { NavService } from '../../_services/_nav.service';

/**
 * This component shows a breadcrumb trail for available routes the router can navigate to.
 * It subscribes to the router in order to update the breadcrumb trail as you navigate to a component.
 */
@Component( {
  selector: 'sd-breadcrumb',
  templateUrl: 'breadcrumb-component.html',
  styleUrls: [ 'breadcrumb.component.css' ],

} )
export class BreadcrumbComponent implements OnInit, OnChanges, OnDestroy {
  @Input() useBootstrap = true;
  @Input() prefix = '';

  public _urls: string[];
  public _routerSubscription: any;

  constructor( private router: Router,
               private _nav: NavService,
               private breadcrumbService: BreadcrumbService ) {
  }

  ngOnInit(): void {
    this.setAllFriendlyName();
    this._urls = new Array();

    if ( this.prefix.length > 0 ) {
      this._urls.unshift( this.prefix );
    }

    this._routerSubscription = this.router.events.subscribe( ( navigationEnd: NavigationEnd ) => {
      if ( navigationEnd instanceof NavigationEnd ) {
        this._urls.length = 0;
        const cleanUrl = this.clearUrl(navigationEnd.urlAfterRedirects || navigationEnd.url);
        this.generateBreadcrumbTrail(cleanUrl);
      }
    } );

    if ( this.router.url != '/' ) {
      this._urls.length = 0; // Fastest way to clear out array
      const cleanUrl = this.clearUrl(this.router.url);
      this.generateBreadcrumbTrail( cleanUrl );
    }

  }

  clearUrl(fullUrl: any){
    if(!fullUrl) return fullUrl;
    const urlWithoutParams = fullUrl.split('?')[0];
    return urlWithoutParams ? urlWithoutParams.split('#')[0] : urlWithoutParams;
  }

  private getCleanUrl(url: string): string {
    const tree = this.router.parseUrl(url);
    return tree.root.children['primary']?.segments
      .map(s => s.path)
      .join('/') || '';
  }


  ngOnChanges( changes: any ): void {
    if ( !this._urls ) {
      return;
    }

    this._urls.length = 0;
    this.generateBreadcrumbTrail( this.router.url );
  }

  generateBreadcrumbTrail( url: string ): void {
    if ( !this.breadcrumbService.isRouteHidden( url ) ) {
      // Add url to beginning of array (since the url is being recursively broken down from full url to its parent)
      // if (this.prefix !== url && url.lastIndexOf('/') !== 0)
      this._urls.unshift( url );
    }

    if ( url.lastIndexOf( '/' ) > 0 ) {
      this.generateBreadcrumbTrail( url.substr( 0, url.lastIndexOf( '/' ) ) ); // Find last '/' and add everything before it as a parent
                                                                               // route
    } else if ( this.prefix.length > 0 ) {
      this._urls.unshift( this.prefix );
    }
  }

  navigateTo( url: string ): void {
    this.router.navigateByUrl( url );
  }

  friendlyName( url: string ): string {
    return !url ? '' : this.breadcrumbService.getFriendlyNameForRoute( url );
  }

  ngOnDestroy(): void {
    this._routerSubscription.unsubscribe();
  }

  setAllFriendlyName() {

    this.breadcrumbService.hideRouteRegex( '/dashboard' );
    this.breadcrumbService.hideRouteRegex( '^/dashboard/[0-9A-Z-]*' );
    this.breadcrumbService.hideRoute( '/dashboard' );
    this.breadcrumbService.hideRoute( '/asi' );
    this.breadcrumbService.hideRoute( '/ter' );
    this.breadcrumbService.hideRoute( '/apl' );
    this.breadcrumbService.hideRoute( '/dor' );
    this.breadcrumbService.hideRoute( '/gcc' );
    this.breadcrumbService.hideRoute( '/sol' );
    this.breadcrumbService.hideRoute( '/pys' );
    this.breadcrumbService.hideRoute( '/gen' );
    this.breadcrumbService.hideRoute( '/ceu' );
    this.breadcrumbService.hideRoute( '/rad' );
    this.breadcrumbService.hideRoute( '/com' );
    this.breadcrumbService.hideRoute( '/des' );
    this.breadcrumbService.hideRoute( '/log' );
    this.breadcrumbService.hideRoute( '/rep' );
    this.breadcrumbService.hideRoute( '/crd' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/constantes', 'Constantes' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/constantes/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/constantes/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/constantes/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/constantes/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/constantes/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/listas', 'Listas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/listas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/listas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/listas/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/listas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/listas/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/seguimientos', 'Eventos de seguimiento' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/central-riesgo-integracion', 'Centrales de integración' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/central-riesgo-integracion/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/central-riesgo-integracion/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/central-riesgo-integracion/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/central-riesgo-integracion/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/central-riesgo-integracion/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/division-politica', 'División Política' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/segmentar-division-politica', 'Segmentar división Política' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/actividad-economica', 'Actividades Económicas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/ocupaciones', 'Ocupaciones' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/administracion-menu', 'Administración de menus' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/grupo-bien', 'Grupos bienes' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/auditoria', 'Auditoria' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/cambiar-contrasena', 'Cambiar contraseña' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/pagos-linea', 'Aplicación pagos en línea' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/funcionalidades', 'Funcionalidades' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/funcionalidades/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/funcionalidades/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/funcionalidades/actualizar/[0-9A-Z-]*' );
// widgets
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/widgets', 'Widgets' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/widgets/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/widgets/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/widgets/actualizar/[0-9A-Z-]*' );

// roles
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/roles', 'Administrador de Roles' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/roles/agregar', 'Nuevo' );
    this.breadcrumbService.hideRoute( '/asi/roles/actualizar' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/asi/roles/actualizar/[0-9A-Z-]*$', 'Detalles' );
    this.breadcrumbService.hideRouteRegex( '^/asi/roles/actualizar/[0-9A-Z-]*/[0-9A-Z-]*$' );
    this.breadcrumbService.hideRouteRegex( '^/asi/roles/actualizar/[0-9A-Z-]*/configurar-funcionalidad$' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/asi/roles/actualizar/[0-9A-Z-]*/configurar-funcionalidad/[0-9A-Z-]*$', 'Funcionalidad' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/usuarios', 'Usuarios' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/usuarios/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/asi/usuarios/agregar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/usuarios/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/usuarios/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/usuarios/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/usuarios/actualizar/[0-9A-Z-]*' );

    // relación de listas
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/configuracion-listas', 'Configuración relación de listas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/configuracion-listas/agregar', 'Nueva relación' );
    this.breadcrumbService.hideRoute( '/asi/configuracion-listas/actualizar' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/asi/configuracion-listas/actualizar/[0-9A-Z-]*$', 'Editar relación' );
    this.breadcrumbService.hideRoute( '/asi/configuracion-listas/detalle' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/asi/configuracion-listas/detalle/[0-9A-Z-]*$', 'Detalle relación' );

    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros', 'Terceros' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros/actualizar', 'Actualizar' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros/cargueMasivo', 'Cargue masivo' );
    this.breadcrumbService.hideRouteRegex( '^/ter/terceros/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ter/terceros/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros/ver-detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ter/terceros/ver-detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/terceros/actualizar-tipo', 'Tipos tercero' );
    this.breadcrumbService.hideRouteRegex( '^/ter/terceros/actualizar-tipo/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/ter/administrar-datos-contacto', 'Administrador datos de contacto' );

    this.breadcrumbService.addFriendlyNameForRoute( '/ter/miperfil', 'Mi perfil' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/miperfil/actualizar', 'Editar' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/canales', 'Canales' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/canales/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/canales/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/canales/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/canales/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/canales/actualizar/[0-9A-Z-]*' );
    // Segmentos
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/segmentos', 'Administrador de Segmentos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/segmentos/agregar', 'Nuevo Segmento' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/segmentos/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/segmentos/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/segmentos/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/segmentos/actualizar/[0-9A-Z-]*' );

    // Aliados
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/aliados', 'Aliados' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/aliados/seleccionar-tercero', 'Seleccionar tercero' );
    this.breadcrumbService.hideRouteRegex( '^/apl/aliados/seleccionar-tercero/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/aliados/sucursales', 'Sucursales' );
    this.breadcrumbService.hideRouteRegex( '^/apl/aliados/sucursales/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/aliados/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/aliados/detalle/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/zonas', 'Zonas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/zonas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/zonas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/zonas/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/zonas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/zonas/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/zonas-traslado', 'Translado de zonas' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estructura-fisica', 'Estructura fisica' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estructura-fisica/asignar-ejecutivo', 'Asignar ejecutivo' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estructura-fisica/asignar-ejecutivo/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estructura-fisica/asignar-zona', 'Asignar zona' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estructura-fisica/asignar-zona/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estructura-fisica/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estructura-fisica/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estructura-fisica/asignar-grupo/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estructura-fisica/asignar-grupo', 'Asignar grupo' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cuestionarios', 'Cuestionarios' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cuestionarios/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cuestionarios/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/cuestionarios/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cuestionarios/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/cuestionarios/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/agenda', 'Agenda' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/agenda/asignar', 'Asignar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/agenda/asignar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/agenda/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/agenda/detalle/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/niveles', 'Niveles' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/niveles/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/niveles/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/niveles/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/niveles/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/niveles/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/niveles/rangoUsuario', 'Rango usuario' );
    this.breadcrumbService.hideRouteRegex( '^/apl/niveles/rangoUsuario/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/preguntas', 'Banco de preguntas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/preguntas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/preguntas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/preguntas/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/preguntas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/preguntas/actualizar/[0-9A-Z-]*' );

    // Estados
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados', 'Estados' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estados/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estados/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados/roles', 'Roles' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estados/roles/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados/botones', 'Configuración de botones' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estados/botones/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estados/causales', 'Causales' );
    this.breadcrumbService.hideRouteRegex( '^/apl/estados/causales/[0-9A-Z-]*' );

    // Perfiles de riesgo
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/perfiles-riesgo', 'Perfiles de riesgo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/perfiles-riesgo/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/perfiles-riesgo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/perfiles-riesgo/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/perfiles-riesgo/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/perfiles-riesgo/actualizar/[0-9A-Z-]*' );

    // Producto
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/producto', 'Productos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/producto/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/producto/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/producto/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/producto/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/producto/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/producto/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/apl/producto/agregar/[0-9A-Z-]*' );

    // Productos troncal
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/productos-troncal', 'Productos troncal' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/productos-troncal/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/productos-troncal/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/productos-troncal/detalle/[0-9A-Za-z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/productos-troncal/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/productos-troncal/actualizar/[0-9A-Za-z-]*' );

    // Centro de costos
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/centro-costo', 'Centro de costos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/centro-costo/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/centro-costo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/dor/centro-costo/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/centro-costo/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/dor/centro-costo/actualizar/[0-9A-Z-]*' );

    // Estructura area
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/estructura-area', 'Estructura area' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/estructura-area/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/estructura-area/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/dor/estructura-area/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/estructura-area/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/dor/estructura-area/actualizar/[0-9A-Z-]*' );

    // Cargos
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/cargo', 'Cargos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/cargo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/dor/cargo/detalle/[0-9A-Z-]*' );

    // Estructura area
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/agrupacion', 'Agrupaciones' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/agrupacion/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/agrupacion/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/dor/agrupacion/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/agrupacion/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/dor/agrupacion/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/agrupacion/oficina-virtual', 'Oficina virtual' );
    this.breadcrumbService.hideRouteRegex( '^/dor/agrupacion/oficina-virtual/[0-9A-Z-]*' );

    // Estructura organizacional
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/estructura-organizacional', 'Estructura organizacional' );

    // Tipo tercero
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/tipo-tercero', 'Tipos de tercero' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/tipo-tercero/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/tipo-tercero/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ter/tipo-tercero/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ter/tipo-tercero/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/ter/tipo-tercero/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantillas', 'Plantillas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantillas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantillas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/plantillas/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantillas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/plantillas/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantilla-parametros', 'Plantilla parametros' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantilla-parametros/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantilla-parametros/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/plantilla-parametros/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/plantilla-parametros/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/plantilla-parametros/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/asi/notificaciones', 'Notificaciones' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/notificaciones/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/notificaciones/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/notificaciones/actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/gcc/centro-llamadas', 'Conctact center' );
    // Politicas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas-credito', 'Políticas de crédito' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas-credito/agregar', 'Nuevo' );
    this.breadcrumbService.hideRoute( '/apl/politicas-credito/actualizar' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/apl/politicas-credito/actualizar/[0-9A-Z-]*$', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/politicas-credito/actualizar/[0-9A-Z-]*/[0-9A-Z-]*$' );
    this.breadcrumbService.hideRoute( '/apl/politicas-credito/detalle' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/apl/politicas-credito/detalle/[0-9A-Z-]*$', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/politicas-credito/detalle/[0-9A-Z-]*/[0-9A-Z-]*$' );

    // Concepto facturacion
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/concepto-facturacion', 'Conceptos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/concepto-facturacion/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/concepto-facturacion/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/concepto-facturacion/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/concepto-facturacion/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/concepto-facturacion/actualizar/[0-9A-Z-]*' );

    // Controles
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/control', 'Controles' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/control/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/control/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/control/actualizar/[0-9A-Z-]*' );

    // Controles
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/palabra-clave', 'Palabras claves' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/palabra-clave/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/palabra-clave/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/palabra-clave/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/palabra-clave/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/palabra-clave/detalle/[0-9A-Z-]*' );

    // Sincronizacion
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sincronizacion', 'Sincronización' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sincronizacion/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sincronizacion/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/sincronizacion/actualizar/[0-9A-Z-]*' );

    // Centro llamadas
    this.breadcrumbService.addFriendlyNameForRoute( '/gcc/configuracion-centro-llamadas', 'Configuración centro de llamadas' );

    // Gestión de solicitud
    this.breadcrumbService.addFriendlyNameForRoute( '/sol/gestion-solicitud', 'Gestión de solicitudes' );
    this.breadcrumbService.addFriendlyNameForRoute( '/sol/gestion-solicitud/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/sol/gestion-solicitud/detalle/[0-9A-Z-]*' );

    //Solicitudes Respuestas
    this.breadcrumbService.addFriendlyNameForRoute( '/sol/solicitud-respuesta', 'Respuestas Solicitudes' );
    this.breadcrumbService.addFriendlyNameForRoute( '/sol/solicitud-respuesta/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/sol/solicitud-respuesta/detalle/[0-9A-Z-]*' );

    // Administrador documento
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/administrador-documento', 'Administrador de documentos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/administrador-documento/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/administrador-documento/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/administrador-documento/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/administrador-documento/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/administrador-documento/detalle/[0-9A-Z-]*' );

    // Administrador de centro de llamadas
    this.breadcrumbService.addFriendlyNameForRoute( '/gcc/administrador-centro-llamadas', 'Administrador de centro de llamadas' );

    // Prospecto
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial', 'Gestión comercial' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-centralizada', 'Gestión centralizada' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-centralizada/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/fabrica-credito', 'Centro de Servicios Digitales' );
    this.breadcrumbService.hideRouteRegex( '^/pys/fabrica-credito/[0-9A-Z-]*' );
    this.breadcrumbService.hideRoute( '/pys/gestion-comercial/prospecto' );
    this.breadcrumbService.hideRoute( '/pys/gestion-comercial/perfilamiento' );
    this.breadcrumbService.hideRoute( '/pys/gestion-comercial/unidad-credito' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/flujo-venta-proceso', 'Flujo de venta' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/editar' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/editar/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/detalle' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/tercero' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/tercero/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/prospecto' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/prospecto/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/preaprobado' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/preaprobado/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/alivios' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/alivios/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/flujo-venta-proceso/simulador/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/llamadas', 'Llamadas' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/llamadas/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/llamadas-proceso', 'Llamadas' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/llamadas-proceso/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/prospecto/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/prospecto/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/prospecto/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/perfilamiento/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/perfilamiento/agregar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/perfilamiento/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/perfilamiento/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/perfilamiento/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/perfilamiento/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/perfilamiento/informacion', 'Información' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/perfilamiento/informacion/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/unidad-credito/informacion', 'Información' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/unidad-credito/informacion/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/unidad-credito/listado', 'Listado unidades' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/unidad-credito/listado/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/unidad-credito/agregar', 'Agregar' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/unidad-credito/agregar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/unidad-credito/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/unidad-credito/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/unidad-credito/detalle-extranjero', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/unidad-credito/detalle-extranjero/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/validar-tercero', 'Validar tercero' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/validar-tercero/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/pys/validar-tercero/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/radicacion', 'Radicación' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/radicacion/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestor-tercero', 'Gestor tercero' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestor-tercero/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/detalle', 'Detalle');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/detalle/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/solicitud-credito', 'Solicitud de crédito');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/solicitud-credito/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/radicacion', 'Radicación');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/radicacion/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestion-tercero', 'Gestión tercero' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestion-tercero/[0-9A-Z-]*' );
    /*Sarlaft gestión documental*/
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestion-documental', 'Gestión Documental Sarlaft 4.0');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestion-documental/[0-9A-Z-]*');

    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestion-documental/detalle', 'Gestión Documental Sarlaft 4.0');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestion-documental/detalle/[0-9A-Z-]*');

    // Tipo causales
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-causal', 'Tipo de causales' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-causal/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-causal/actualizar/[0-9A-Z-]*' );

    // Configuracion tipo de ocupacion
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-ocupacion-configuracion', 'Configuración tipo ocupación' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-ocupacion-configuracion/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-ocupacion-configuracion/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-ocupacion-configuracion/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-ocupacion-configuracion/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-ocupacion-configuracion/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-ocupacion-configuracion/CapacidadPago', 'Capacidad de pago' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-ocupacion-configuracion/CapacidadPago/[0-9A-Z-]*' );
    // Requisitos
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/requisitos', 'Requisitos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/requisitos/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/requisitos/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/requisitos/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/requisitos/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/requisitos/actualizar/[0-9A-Z-]*' );

    // Ejecutivo
    this.breadcrumbService.addFriendlyNameForRoute( '/gen/ejecutivo', 'Ejecutivo' );
    this.breadcrumbService.hideRouteRegex( '^/gen/ejecutivo/[0-9A-Z-]*' );

    // Producto agricola
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/producto-agricola', 'Producto agricola' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/producto-agricola/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/producto-agricola/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/producto-agricola/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/producto-agricola/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/producto-agricola/actualizar/[0-9A-Z-]*' );

    // Cuentas contables
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cuenta-contable', 'Cuentas contables' );

    // Estructuras agrícolas
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-agricola', 'Estructuras agrícolas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-agricola/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-agricola/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/estructura-agricola/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-agricola/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/estructura-agricola/actualizar/[0-9A-Z-]*' );

    // Estructuras pecuarias
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-pecuaria', 'Estructuras pecuarias' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-pecuaria/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-pecuaria/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/estructura-pecuaria/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/estructura-pecuaria/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/estructura-pecuaria/actualizar/[0-9A-Z-]*' );

    // Estructuras fisicas con pecuarias y agricolas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/estructura-fisica/estructura-oficina', 'Asignar' );
    this.breadcrumbService.hideRouteRegex( '/apl/estructura-fisica/estructura-oficina/[0-9A-Z-]*' );
    // Tasas variables
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tasas-variables', 'Tasas variables' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tasas-variables/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tasas-variables/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tasas-variables/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tasas-variables/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tasas-variables/detalle/[0-9A-Z-]*' );
    // Tipo respaldo
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-respaldo', 'Tipo respaldo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-respaldo/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-respaldo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-respaldo/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-respaldo/detalle-subcategoria', 'Detalle subcategoría' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-respaldo/detalle-subcategoria/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-respaldo/configurar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-respaldo/configurar/[0-9A-Z-]*' );

    // Seguro
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/seguro', 'Seguros' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/seguro/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/seguro/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/seguro/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/seguro/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/seguro/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/seguro/meta-venta', 'Meta Venta' );
    //this.breadcrumbService.hideRouteRegex( '^/apl/seguro/meta-venta' );

    // Insumo Pecuario
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-pecuario', 'Insumo pecuario' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-pecuario/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-pecuario/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/insumo-pecuario/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-pecuario/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/insumo-pecuario/actualizar/[0-9A-Z-]*' );

    // Insumo Agricola
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-agricola', 'Insumo agrícola' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-agricola/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-agricola/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/insumo-agricola/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-agricola/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/insumo-agricola/actualizar/[0-9A-Z-]*' );

    // Vinculacion terceros
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vinculacion-tercero', 'Vinculación de terceros' );
    //this.breadcrumbService.addFriendlyNameForRoute( '/ceu/insumo-pecuario/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vinculacion-tercero/configurar-vinculacion', 'Configurar vinculación' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vinculacion-tercero/configurar-vinculacion/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vinculacion-tercero/configurar-vinculacion/detalle-submenu', 'Detalle submenú' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vinculacion-tercero/configurar-vinculacion/detalle-submenu/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vinculacion-tercero/configurar-ocupacion', 'Configurar ocupación' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vinculacion-tercero/configurar-ocupacion/[0-9A-Z-]*' );

    // Menú visita
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/menu-visita', 'Menú visita' );

    // Proceso Productivo
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/proceso-productivo', 'Proceso productivo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/proceso-productivo/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/proceso-productivo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/proceso-productivo/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/proceso-productivo/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/proceso-productivo/actualizar/[0-9A-Z-]*' );

    // Centro de llamadas
    this.breadcrumbService.addFriendlyNameForRoute( '/gcc/centro-llamadas', 'Centro de llamadas' );

    // Plaga
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plaga', 'Plaga' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plaga/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/apl/plaga/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plaga/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/plaga/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plaga/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/plaga/detalle/[0-9A-Z-]*' );

    // Forma desembolso
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/forma-desembolso', 'Especies' );

    // Modalidades
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/modalidades', 'Modalidades' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/modalidades/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/apl/modalidades/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/modalidades/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/modalidades/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/modalidades/detalle', 'Detalle' );

    // Alertas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/alertas', 'Alertas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/alertas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/alertas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/alertas/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/alertas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/alertas/actualizar/[0-9A-Z-]*' );

    // Convenio
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/convenios', 'Convenios' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/convenios/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/convenios/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/convenios/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/convenios/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/convenios/actualizar/[0-9A-Z-]*' );

    // Vinculación Estado Financiero
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vinculacion-estado-financiero', 'Vinculación estado financiero' );

    // Historia Crédito
    this.breadcrumbService.addFriendlyNameForRoute( '/rad/historia-credito', 'Historia de crédito' );

    // Novedades
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/novedades', 'Novedades' );
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/novedades/ejecutivo', 'Ejecutivo' );

    this.breadcrumbService.addFriendlyNameForRoute( '/com/gestor-comite', 'Gestor de comite' );
    this.breadcrumbService.addFriendlyNameForRoute( '/com/gestor-comite/comite', 'Comite' );
    this.breadcrumbService.hideRouteRegex( '^/com/gestor-comite/comite/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/com/gestor-comite/gestor-aprobacion', 'Gestor de aprobación' );

    // Desembolso
    this.breadcrumbService.addFriendlyNameForRoute( '/des/desembolso', 'Desembolso' );
    this.breadcrumbService.hideRouteRegex( '^/des/desembolso/[0-9A-Z-]*' );
    // Cambio perfil riesgo
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cambio-perfil', 'Cambio Perfil' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/cambio-perfil/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/cambio-perfil/actualizar/[0-9A-Z-]*' );

    // Linea credito
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/homologacion-estado', 'Homologación de estados' );
    // Fases
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/fases', 'Fases' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/fases/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/fases/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/fases/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/fases/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/fases/actualizar/[0-9A-Z-]*' );

    // Homologacion listas
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/homologacion-listas', 'Homologación de listas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/homologacion-listas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/homologacion-listas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/asi/homologacion-listas/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/asi/homologacion-listas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/asi/homologacion-listas/actualizar/[0-9A-Z-]*' );


    // Regional
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/regional', 'Regionales' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/regional/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/regional/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/dor/regional/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/dor/regional/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/dor/regional/actualizar/[0-9A-Z-]*' );

    // Excepciones-gmf
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/excepciones-gmf', 'Excepciones GMF' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/excepciones-gmf/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/apl/excepciones-gmf/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/excepciones-gmf/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/excepciones-gmf/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/excepciones-gmf/detalle', 'Detalle' );

    // Campañas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/campanias', 'Campañas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/campanias/agregar', 'Nuevo' );
    this.breadcrumbService.hideRouteRegex( '^/apl/campanias/detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/campanias/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/campanias/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/campanias/detalle', 'Detalle' );

    // Sistemas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sistemas', 'Sistemas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sistemas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sistemas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/sistemas/actualizar/[0-9A-Z-]*' );

    // Configuracion Url Externas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/extensiones-telefonicas', 'Extensiones Telefónicas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/extensiones-telefonicas/agregar', 'Nueva' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/extensiones-telefonicas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/extensiones-telefonicas/actualizar/[0-9A-Z-]*' );

    // Configuracion Url Externas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/banco-error-integracion', 'Banco de Errores' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/banco-error-integracion/agregar', 'Nueva' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/banco-error-integracion/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/banco-error-integracion/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/banco-error-integracion/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/banco-error-integracion/detalle/[0-9A-Z-]*' );

    // Bloqueos Sarlaft
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/bloqueo-sarlaft', 'Bloqueos por Sarlaft' );


    // Solicitud Log Errores
    this.breadcrumbService.addFriendlyNameForRoute( '/log/solicitud-log-errores', 'Log Errores Solicitudes' );
    this.breadcrumbService.addFriendlyNameForRoute( '/log/solicitud-log-errores/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/log/solicitud-log-errores/detalle/[0-9A-Z-]*' );

    // Solicitud Log Errores
    this.breadcrumbService.addFriendlyNameForRoute( '/log/auditoria-integraciones', 'Auditoría integraciones' );

    // Auditoria tercero
    this.breadcrumbService.addFriendlyNameForRoute( '/log/auditoria-terceros', 'Auditoría terceros' );

    // reportes
    this.breadcrumbService.addFriendlyNameForRoute( '/rep/reportes', 'Reportes' );

    // Flujo de venta
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/flujo-venta', 'Flujo de venta' );

    // Paquete seguros
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/paquete-seguros', 'Paquetes seguros' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/paquete-seguros/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/paquete-seguros/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/paquete-seguros/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/paquete-seguros/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/paquete-seguros/detalle/[0-9A-Z-]*' );

    // Matriz identidad
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/matriz-identidad', 'Matriz de Validación identidad' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/matriz-identidad/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/matriz-identidad/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/matriz-identidad/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/matriz-identidad/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/matriz-identidad/detalle/[0-9A-Z-]*' );


    // Flujo de venta proceso
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/flujo-venta-proceso', 'Flujo de venta' );
    this.breadcrumbService.hideRouteRegex( '^/pys/flujo-venta-proceso/[0-9A-Z-]*' );

    // Matriz identidad
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/garantia-idoneas', 'Fondos de Garantías' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/garantia-idoneas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/garantia-idoneas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/garantia-idoneas/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/garantia-idoneas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/garantia-idoneas/detalle/[0-9A-Z-]*' );

    // Administrador de Garantías
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/administrador-garantias-idoneas', 'Configuración Garantías' );

    // Administrador de servicios bancarios
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/administrador-servicios-bancarios', 'Administrador de servicios bancarios' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/administrador-servicios-bancarios/titular', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/administrador-servicios-bancarios/titular/[0-9A-Za-z-]*' );

    // tipo productores
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-productores', 'Tipo productor' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-productores/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-productores/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-productores/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tipo-productores/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/tipo-productores/detalle/[0-9A-Z-]*' );

    // configuracion canales estados
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/configuracion-canal-estado', 'Configuración por canal' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/configuracion-canal-estado/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/configuracion-canal-estado/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/configuracion-canal-estado/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/configuracion-canal-estado/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/configuracion-canal-estado/detalle/[0-9A-Z-]*' );

    // Porcentaje Labores Culturales
    this.breadcrumbService.addFriendlyNameForRoute( '/ceu/porcentaje-labores-culturales', 'Porcentaje Labores Culturales' );

    // Porcentaje Labores Culturales Agricola
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/ceu/porcentaje-labores-culturales/agricola/porcentaje-labor-cultural-insumo/[0-9A-Z-]*$', 'Configuración General' );
    this.breadcrumbService.hideRoute( '/ceu/porcentaje-labores-culturales/agricola' );
    this.breadcrumbService.hideRoute( '/ceu/porcentaje-labores-culturales/agricola/porcentaje-labor-cultural-insumo' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/porcentaje-labores-culturales/agricola/porcentaje-labor-cultural-insumo/[0-9A-Z-]*/[0-9A-Z-]*$' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/porcentaje-labores-culturales/agricola/porcentaje-labor-cultural-insumo/[0-9A-Z-]*/porcentaje-labor-cultural-insumo-costo$' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/ceu/porcentaje-labores-culturales/agricola/porcentaje-labor-cultural-insumo/[0-9A-Z-]*/porcentaje-labor-cultural-insumo-costo/[0-9A-Z-]*$', 'Configuración Estructura Agrícola' );

    // Porcentaje Labores Culturales Pecuario
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/ceu/porcentaje-labores-culturales/pecuario/porcentaje-labor-cultural-insumo/[0-9A-Z-]*$', 'Configuración General' );
    this.breadcrumbService.hideRoute( '/ceu/porcentaje-labores-culturales/pecuario' );
    this.breadcrumbService.hideRoute( '/ceu/porcentaje-labores-culturales/pecuario/porcentaje-labor-cultural-insumo' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/porcentaje-labores-culturales/pecuario/porcentaje-labor-cultural-insumo/[0-9A-Z-]*/[0-9A-Z-]*$' );
    this.breadcrumbService.hideRouteRegex( '^/ceu/porcentaje-labores-culturales/pecuario/porcentaje-labor-cultural-insumo/[0-9A-Z-]*/porcentaje-labor-cultural-insumo-costo$' );
    this.breadcrumbService.addFriendlyNameForRouteRegex( '^/ceu/porcentaje-labores-culturales/pecuario/porcentaje-labor-cultural-insumo/[0-9A-Z-]*/porcentaje-labor-cultural-insumo-costo/[0-9A-Z-]*$', 'Configuración Estructura Pecuaria' );

    // Politicas
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas', this._nav.buscarMensaje('POLITICAS.LBL_POLITICAS_PRODUCTO') );

    // Unidades de medida
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/unidades-medida', 'Unidades de Medida' );
    // Regla de negocio
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/reglas-negocio', 'Reglas de Negocio' );

    //Gestor comite de creditos
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestor-comite-credito', 'Comité de Crédito' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestor-comite-credito/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/detalle-comite', 'Comité de Crédito' );
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/detalle-comite/[0-9A-Z-]*' );
    // Marca blanca
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/convenio', 'Convenio' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/convenio/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/convenio/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/crd/convenio/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/convenio/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/crd/convenio/detalle/[0-9A-Z-]*' );


    // Flujo de crédito digital
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/flujo-creditos-digitales', 'Flujo de crédito digital' );

    // Mensaje
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/mensajes', 'Administrador de Mensajes' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/mensajes/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/mensajes/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/crd/mensajes/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/mensajes/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/crd/mensajes/detalle/[0-9A-Z-]*' );

    // Productos
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/productos-agrupaciones', 'Administrador de Agrupación Productos' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/productos-agrupaciones/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/productos-agrupaciones/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/crd/productos-agrupaciones/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/productos-agrupaciones/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/crd/productos-agrupaciones/detalle/[0-9A-Z-]*' );

    // Despensas
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/despensas', 'Despensas' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/despensas/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/despensas/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/crd/despensas/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/despensas/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/crd/despensas/detalle/[0-9A-Z-]*' );

    // Niveles de riesgo
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/niveles-riesgos', 'Niveles de riesgo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/niveles-riesgos/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/niveles-riesgos/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/crd/niveles-riesgos/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/niveles-riesgos/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/crd/niveles-riesgos/detalle/[0-9A-Z-]*' );

    // Plan de cargo
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plan-cargo', 'Plan de cargo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plan-cargo/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plan-cargo/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/plan-cargo/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/plan-cargo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/plan-cargo/detalle/[0-9A-Z-]*' );

    // Politicas duras
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas-duras', 'Políticas duras' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas-duras/agregar', 'Nuevo' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas-duras/actualizar', 'Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/politicas-duras/actualizar/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/politicas-duras/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/politicas-duras/detalle/[0-9A-Z-]*' );


    // Pagaduria
    this.breadcrumbService.addFriendlyNameForRoute('/apl/pagaduria/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/pagaduria', 'Pagaduria');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/pagaduria/editar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/pagaduria/editar/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/pagaduria/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/pagaduria/detalle/[0-9A-Z-]*' );

    // Comisiones freelance
    this.breadcrumbService.addFriendlyNameForRoute('/apl/comision-freelance/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/comision-freelance', 'Comisiones Freelance');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/comision-freelance/actualizar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/comision-freelance/actualizar/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/comision-freelance/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/comision-freelance/detalle/[0-9A-Z-]*' );

    // Homologacion planes cargo
    this.breadcrumbService.addFriendlyNameForRoute('/apl/homologacion-plan-cargo/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/homologacion-plan-cargo', 'Homologaciones planes de cargos');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/homologacion-plan-cargo/actualizar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/homologacion-plan-cargo/actualizar/[0-9A-Z-]*');

    // Convenios planes cargo
    this.breadcrumbService.addFriendlyNameForRoute('/apl/convenio-plan-cargo/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/convenio-plan-cargo', 'Convenio planes de cargos');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/convenio-plan-cargo/actualizar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/convenio-plan-cargo/actualizar/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/convenio-plan-cargo/detalle', 'Detalle' );
    this.breadcrumbService.hideRouteRegex( '^/apl/convenio-plan-cargo/detalle/[0-9A-Z-]*' );

    // Servidores alfresco
    this.breadcrumbService.addFriendlyNameForRoute('/apl/servidor-alfresco/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/servidor-alfresco', 'Servidores alfresco');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/servidor-alfresco/actualizar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/servidor-alfresco/actualizar/[0-9A-Z-]*');

    // Estimador de gastos flujo digital
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/estimador-gastos', 'Estimador de gastos' );
    // Configurar intentos
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/configurar-variables', 'Configurar variables Digital' );
    // Dispersion
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/dispersion', 'Dispersión de pagos' );

    // Solicitud Log Errores
    this.breadcrumbService.addFriendlyNameForRoute( '/crd/gestionar-plataforma-digital', 'Solicitudes en Gestión Plataforma Digital' );

    // Apl Nivel de Riesgo Sarlaft
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sarlaft', 'Nivel de Riesgo Sarlaft 4.0' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sarlaft/niveles-agregar', 'Nuevo Nivel' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sarlaft/niveles-actualizar', 'Niveles Actualizar' );
    this.breadcrumbService.hideRouteRegex( '^/apl/sarlaft/niveles-actualizar/[0-9A-Z-]*' );

    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sarlaft/variables-agregar', 'Nueva Variable' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sarlaft/variables-detalle', 'Detalle Variable' );
    this.breadcrumbService.hideRouteRegex( '^/apl/sarlaft/variables-detalle/[0-9A-Z-]*' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/sarlaft/variables-actualizar', 'Actualizar Variable' );
    this.breadcrumbService.hideRouteRegex( '^/apl/sarlaft/variables-actualizar/[0-9A-Z-]*' );

    // Consumo Nivel de Riesgo Sarlaft
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestion-riesgo', 'Gestión Riesgo Sarlaft 4.0');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestion-riesgo/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/pys/gestion-comercial/gestion-riesgo/detalle', 'Gestión Riesgo Sarlaft 4.0');
    this.breadcrumbService.hideRouteRegex( '^/pys/gestion-comercial/gestion-riesgo/detalle/[0-9A-Z-]*');

    // Simulador de credito
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/simulador-credito', 'Simulador de Crédito' );

    // Topes inversiones
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tope-inversion', 'Topes de inversión' );
    this.breadcrumbService.addFriendlyNameForRoute('/apl/tope-inversion/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/tope-inversion/actualizar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/tope-inversion/actualizar/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tope-inversion/detalle', 'Detalle');
    this.breadcrumbService.hideRouteRegex( '^/apl/tope-inversion/detalle/[0-9A-Z-]*');

    // Vigencias APL
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vigencias', 'Configuración de Vigencias' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vigencias/validity-requests-edit', 'Actualizar vigencia de la solicitud' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vigencias/validity-employees-edit', 'Actualizar vigencia del empleado' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vigencias/administration-documents-add', 'Agregar vigencia del documento' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vigencias/administration-documents-edit', 'Actualizar vigencia del documento' );
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/vigencias/administration-documents-detail', 'Detalle del documento' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vigencias/administration-documents-detail/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vigencias/administration-documents-edit/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vigencias/validity-employees-edit/[0-9A-Z-]*' );
    this.breadcrumbService.hideRouteRegex( '^/apl/vigencias/validity-requests-edit/[0-9A-Z-]*' );

    // Topes inversiones
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tope-inversion', 'Topes de inversión' );
    this.breadcrumbService.addFriendlyNameForRoute('/apl/tope-inversion/agregar', 'Agregar');
    this.breadcrumbService.addFriendlyNameForRoute('/apl/tope-inversion/actualizar', 'Actualizar');
    this.breadcrumbService.hideRouteRegex('^/apl/tope-inversion/actualizar/[0-9A-Z-]*');
    this.breadcrumbService.addFriendlyNameForRoute( '/apl/tope-inversion/detalle', 'Detalle');
    this.breadcrumbService.hideRouteRegex( '^/apl/tope-inversion/detalle/[0-9A-Z-]*');
  }
}
